/*
 * Mother of Macros: rebuilt one-time (à la carte) menu and its plan step.
 * Vanilla JS, no jQuery. Loaded by sections/a-la-carte-menu.liquid.
 *
 * Menu ([data-alc-menu]):
 *   - Two steps on one page: 1 = entrées, "Continue to Desserts" (always active);
 *     2 = desserts, "Back to Entrées" and the checkout button (disabled until the minimum).
 *     #menu / #desserts in the URL, so the browser Back button moves between them.
 *   - Selections are Shopify cart lines with the property _mom_alc_item, so they survive
 *     step changes, reloads and the cart drawer. Taps update the screen at once; the cart is
 *     synced in the background, one request chain at a time.
 *   - ats.js keeps exactly one Freeze Pack with these lines. The 60-pack builders clear them
 *     on entry; this menu removes subscription lines on entry, so the two never mix.
 *   - Order minimum and progress bar: assets/mom-minimum.js (shared with the cart drawer).
 *   - The cards reuse the subscription builder's class names for identical styling.
 *     Their clicks are stopped at the section so the new-pdp handlers in custom.js
 *     (which listen on document for the same class names) never run here.
 *
 * Plan step ([data-alc-flow], sections/build-a-box.liquid in "rebuilt" mode):
 *   ZIP -> plan -> menu on one page. Subscription keeps the legacy redirects.
 */
(function () {
  'use strict';

  // Both the Build-a-Box section (plan step) and the menu section include this file.
  if (window.MOMAlaCarteLoaded) return;
  window.MOMAlaCarteLoaded = true;

  var ALC_PROPERTY = '_mom_alc_item';
  var BOX_PROPERTY = '_mom_box_item';
  var MENU_HASHES = ['#menu', '#desserts'];
  var DUPLICATE_TAP_MS = 80;
  var SYNC_DELAY_MS = 250;
  var MAX_QUANTITY = 99;
  var LEGACY_STORAGE_KEYS = ['build_a_box', 'build_a_box_zip', 'build_a_box_saved_at'];

  /* ---------- helpers ---------- */

  function getZip() {
    var match = document.cookie.match(/(?:^|;\s*)buildabox_zip=([^;]*)/);
    return match ? decodeURIComponent(match[1]).trim() : '';
  }

  function readLocalZips(element) {
    return (element.getAttribute('data-local-zips') || '')
      .split(',')
      .map(function (zip) { return zip.trim(); })
      .filter(Boolean);
  }

  function zoneFor(zip, localZips) {
    return localZips.indexOf(zip) !== -1 ? 'local' : 'nationwide';
  }

  function formatMoney(cents, format) {
    if (window.MOMMinimum) return window.MOMMinimum.formatMoney(cents, format);
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  var lastTapAt = typeof WeakMap === 'function' ? new WeakMap() : null;

  // A repeat activation of the same control inside 80ms is a duplicate click from one tap.
  function isDuplicateTap(element) {
    if (!lastTapAt) return false;
    var now = Date.now();
    var last = lastTapAt.get(element) || 0;
    lastTapAt.set(element, now);
    return now - last < DUPLICATE_TAP_MS;
  }

  function cartRequest(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data && (data.description || data.message) || 'Cart request failed');
          error.status = response.status;
          throw error;
        }
        return data;
      });
    });
  }

  function isAlcLine(item) {
    return Boolean(item && item.properties && String(item.properties[ALC_PROPERTY]) === 'true');
  }

  function isSubscriptionLine(item) {
    return Boolean(item && (item.selling_plan_allocation ||
      (item.properties && String(item.properties[BOX_PROPERTY]) === 'true')));
  }

  /* ---------- menu ---------- */

  function AlaCarteMenu(root) {
    this.root = root;
    this.zip = getZip();
    this.localZips = readLocalZips(root);
    this.zone = root.getAttribute('data-zone') || zoneFor(this.zip, this.localZips);
    this.steps = parseInt(root.getAttribute('data-steps'), 10) || 1;
    this.freezeVariantId = String(root.getAttribute('data-freeze-variant') || '');
    this.moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';

    this.zoneElement = root.querySelector('[data-alc-zone="' + this.zone + '"]');
    this.panel = root.querySelector('[data-alc-panel]');
    this.itemsElement = root.querySelector('[data-alc-items]');
    this.emptyElement = root.querySelector('[data-alc-empty]');
    this.countElements = root.querySelectorAll('[data-alc-count]');
    this.subtotalElement = root.querySelector('[data-alc-subtotal]');
    this.minBar = root.querySelector('[data-alc-min-bar]');
    this.checkoutButton = root.querySelector('[data-alc-action="checkout"]');
    this.errorElement = root.querySelector('[data-alc-error]');
    this.dialog = root.querySelector('[data-alc-dialog]');
    this.dialogContent = root.querySelector('[data-alc-dialog-content]');
    this.dialogBuy = root.querySelector('[data-alc-dialog-buy]');
    this.dialogProductId = null;
    this.dialogOpener = null;

    this.products = {};
    this.productOrder = [];
    this.quantities = {};      // what the shopper chose (shown at once)
    this.cart = null;          // last /cart.js
    this.dirty = false;        // local choices not yet in the cart
    this.syncing = null;       // running sync promise
    this.syncTimer = null;

    LEGACY_STORAGE_KEYS.forEach(function (key) {
      try { window.localStorage.removeItem(key); } catch (error) { /* storage unavailable */ }
    });

    this.collectProducts();
    this.readInitialQuantities();
    this.bind();
    this.render();
    this.trackPanelHeight();
    this.loadCart();
    this.trackListView();
    var menu = this;
    // Opening the page straight on the menu (#menu / #desserts): send the list view again once
    // the page has loaded, so GTM is listening. trackListView sends each list only once.
    function resendListView() {
      var sent = (window.dataLayer || []).some(function (entry) { return entry && entry.event === 'build_a_box'; });
      if (!sent) menu.trackedLists = {};
      menu.trackListView();
    }
    if (document.readyState === 'complete') window.setTimeout(resendListView, 0);
    else window.addEventListener('load', resendListView);
    document.addEventListener('alc:menu-shown', function () { menu.setStep(1, false); });
  }

  AlaCarteMenu.prototype.collectProducts = function () {
    if (!this.zoneElement) return;
    var cards = this.zoneElement.querySelectorAll('[data-alc-card]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var id = String(card.getAttribute('data-id'));
      if (!id || id === this.freezeVariantId) continue;
      if (!this.products[id]) {
        this.products[id] = {
          id: id,
          title: card.getAttribute('data-title') || '',
          productId: card.getAttribute('data-product-id') || '',
          url: card.getAttribute('data-url') || '',
          price: parseInt(card.getAttribute('data-price'), 10) || 0,
          image: card.getAttribute('data-image') || '',
          available: card.getAttribute('aria-disabled') !== 'true',
          cards: []
        };
        this.productOrder.push(id);
      }
      this.products[id].cards.push(card);
    }
  };

  // Quantities already in the cart, printed by Liquid: cards render filled before any fetch.
  AlaCarteMenu.prototype.readInitialQuantities = function () {
    var self = this;
    (this.root.getAttribute('data-cart-quantities') || '').split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var id = parts[0];
      var quantity = parseInt(parts[1], 10) || 0;
      if (self.products[id] && quantity > 0) self.quantities[id] = (self.quantities[id] || 0) + quantity;
    });
  };

  /* ---------- cart ---------- */

  AlaCarteMenu.prototype.loadCart = function () {
    var self = this;
    return cartRequest('/cart.js').then(function (cart) {
      self.cart = cart;
      return self.cleanCart(cart);
    }).then(function () {
      if (!self.dirty) self.adoptCart();
      self.render();
    }).catch(function () { /* the menu still works from the printed quantities */ });
  };

  // Entering the one-time menu: remove subscription lines (they must never mix with one-time
  // items) and one-time lines that are not on this ZIP's menu (other zone or past week).
  AlaCarteMenu.prototype.cleanCart = function (cart) {
    var self = this;
    var updates = {};
    (cart.items || []).forEach(function (item) {
      if (isSubscriptionLine(item)) updates[item.key] = 0;
      else if (isAlcLine(item) && !self.products[String(item.variant_id)]) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    return cartRequest('/cart/update.js', { updates: updates }).then(function (updated) {
      self.cart = updated;
      self.refreshCartUi();
    });
  };

  // Take the quantities from the cart (after another tab or the cart drawer changed it).
  AlaCarteMenu.prototype.adoptCart = function () {
    var self = this;
    var quantities = {};
    ((this.cart && this.cart.items) || []).forEach(function (item) {
      var id = String(item.variant_id);
      if (isAlcLine(item) && self.products[id]) quantities[id] = (quantities[id] || 0) + item.quantity;
    });
    this.quantities = quantities;
  };

  AlaCarteMenu.prototype.scheduleSync = function () {
    var self = this;
    this.dirty = true;
    window.clearTimeout(this.syncTimer);
    this.syncTimer = window.setTimeout(function () { self.sync(); }, SYNC_DELAY_MS);
  };

  // Bring the cart in line with this.quantities. Runs one chain at a time and repeats while
  // the shopper keeps tapping, so rapid taps never race each other.
  AlaCarteMenu.prototype.sync = function () {
    var self = this;
    window.clearTimeout(this.syncTimer);
    if (this.syncing) return this.syncing.then(function () { return self.dirty ? self.sync() : null; });

    this.dirty = false;
    var wanted = JSON.parse(JSON.stringify(this.quantities));

    this.syncing = cartRequest('/cart.js').then(function (cart) {
      var lines = {};
      (cart.items || []).forEach(function (item) {
        if (isAlcLine(item)) lines[String(item.variant_id)] = item;
      });
      var adds = [];
      var updates = {};
      Object.keys(wanted).forEach(function (id) {
        var line = lines[id];
        // Increases always go through /cart/add.js (the added amount), so Stape's
        // add_to_cart_stape -> GA4 add_to_cart + Meta AddToCart counts every "+".
        // Decreases use /cart/update.js.
        var have = line ? line.quantity : 0;
        if (wanted[id] > have) adds.push({ id: Number(id), quantity: wanted[id] - have, properties: { _mom_alc_item: 'true' } });
        else if (line && wanted[id] < have) updates[line.key] = wanted[id];
      });
      Object.keys(lines).forEach(function (id) {
        if (!wanted[id]) updates[lines[id].key] = 0;
      });

      var chain = Promise.resolve();
      if (Object.keys(updates).length) chain = chain.then(function () { return cartRequest('/cart/update.js', { updates: updates }); });
      if (adds.length) chain = chain.then(function () { return cartRequest('/cart/add.js', { items: adds }); });
      return chain;
    }).then(function () {
      return cartRequest('/cart.js');
    }).then(function (cart) {
      self.cart = cart;
      self.hideError();
      self.refreshCartUi();
      // Something else changed the cart meanwhile (e.g. the 5-hour cart expiry cleared it):
      // apply the selection once more.
      if (!self.cartMatches(wanted) && !self.retried) {
        self.retried = true;
        self.dirty = true;
      }
    }).catch(function (error) {
      // e.g. not enough stock: show the message and fall back to what the cart holds.
      self.showError(error && error.message ? error.message : 'Your order could not be updated. Please try again.');
      return cartRequest('/cart.js').then(function (cart) {
        self.cart = cart;
        self.dirty = false;
        self.adoptCart();
      }).catch(function () {});
    }).then(function () {
      self.syncing = null;
      self.render();
      if (self.dirty) return self.sync();
      return null;
    });

    return this.syncing;
  };

  AlaCarteMenu.prototype.cartMatches = function (wanted) {
    var inCart = {};
    ((this.cart && this.cart.items) || []).forEach(function (item) {
      if (isAlcLine(item)) inCart[String(item.variant_id)] = (inCart[String(item.variant_id)] || 0) + item.quantity;
    });
    var ids = Object.keys(wanted).concat(Object.keys(inCart));
    return ids.every(function (id) { return (wanted[id] || 0) === (inCart[id] || 0); });
  };

  // Header cart count and cart drawer, re-rendered by Shopify (same sections the theme uses).
  AlaCarteMenu.prototype.refreshCartUi = function () {
    var self = this;
    window.clearTimeout(this.cartUiTimer);
    this.cartUiTimer = window.setTimeout(function () {
      fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
        .then(function (response) { return response.json(); })
        .then(function (sections) {
          var parser = new DOMParser();
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            var bubbleDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
            var bubbleSection = bubbleDoc.querySelector('.shopify-section');
            if (bubbleSection) bubble.innerHTML = bubbleSection.innerHTML;
          }
          var drawer = document.getElementById('CartDrawer');
          if (drawer && sections['cart-drawer']) {
            var drawerDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
            var newDrawer = drawerDoc.querySelector('#CartDrawer');
            if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
            var element = document.querySelector('cart-drawer');
            if (element) element.classList.toggle('is-empty', !(self.cart && self.cart.item_count));
          }
        })
        .catch(function () { /* the drawer refreshes itself when opened */ });
    }, 200);
  };

  /* ---------- events ---------- */

  AlaCarteMenu.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var control = event.target.closest('[data-alc-action]');
      if (!control || !self.root.contains(control)) return;

      // Keep the subscription builder handlers in custom.js (same class names) out.
      event.stopPropagation();
      if (control.tagName === 'A') event.preventDefault();
      if (control.disabled || isDuplicateTap(control)) return;

      var action = control.getAttribute('data-alc-action');
      var id = self.productIdFor(control);

      if (action === 'increase') self.setQuantity(id, self.quantityOf(id) + 1);
      else if (action === 'decrease') self.setQuantity(id, self.quantityOf(id) - 1);
      else if (action === 'remove') self.setQuantity(id, 0);
      else if (action === 'details') self.openDetails(id, control);
      else if (action === 'continue') self.goToStep(2);
      else if (action === 'back') self.backToEntrees();
      else if (action === 'checkout') self.checkout();
    });

    if (this.dialog) {
      this.dialog.addEventListener('click', function (event) {
        if (event.target === self.dialog || event.target.closest('[data-alc-dialog-close]')) {
          self.closeDetails();
        }
      });
      this.dialog.addEventListener('close', function () {
        self.dialogProductId = null;
        if (self.dialogOpener && typeof self.dialogOpener.focus === 'function') self.dialogOpener.focus();
        self.dialogOpener = null;
      });
    }

    // Cart changed elsewhere (cart drawer, another tab): take its quantities when idle.
    if (window.MOMMinimum) {
      window.MOMMinimum.onChange(function (cart) {
        if (self.syncing || self.dirty) return;
        self.cart = cart;
        self.adoptCart();
        self.render();
      });
    }

    window.addEventListener('popstate', function () {
      self.setStep(window.location.hash === '#desserts' ? 2 : 1, false);
    });

    // Returning from checkout with the back button restores this page from cache.
    window.addEventListener('pageshow', function (event) {
      if (self.checkoutButton) self.checkoutButton.classList.remove('active-loading');
      if (event.persisted) self.loadCart();
    });
  };

  AlaCarteMenu.prototype.productIdFor = function (control) {
    if (control.hasAttribute('data-id')) return String(control.getAttribute('data-id'));
    var card = control.closest('[data-alc-card]');
    if (card) return String(card.getAttribute('data-id'));
    if (this.dialog && this.dialog.contains(control)) return this.dialogProductId;
    return null;
  };

  AlaCarteMenu.prototype.quantityOf = function (id) {
    return this.quantities[id] || 0;
  };

  AlaCarteMenu.prototype.setQuantity = function (id, quantity) {
    var product = id && this.products[id];
    if (!product || !product.available) return;
    quantity = Math.max(0, Math.min(MAX_QUANTITY, quantity));
    if (quantity === 0) delete this.quantities[id];
    else this.quantities[id] = quantity;
    this.retried = false;
    this.render();
    this.scheduleSync();
  };

  /* ---------- steps ---------- */

  AlaCarteMenu.prototype.step = function () {
    return parseInt(this.root.getAttribute('data-step'), 10) || 1;
  };

  AlaCarteMenu.prototype.setStep = function (step, scroll) {
    if (this.steps < 2) step = 1;
    this.root.setAttribute('data-step', String(step));
    this.render();
    this.trackListView();
    if (scroll) {
      var top = this.root.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo(0, Math.max(0, top));
    }
  };

  AlaCarteMenu.prototype.goToStep = function (step) {
    if (this.steps < 2) return;
    var hash = step === 2 ? '#desserts' : '#menu';
    if (window.location.hash !== hash) {
      window.history.pushState({ alcView: 'menu', alcStep: step }, '', window.location.pathname + window.location.search + hash);
    }
    this.setStep(step, true);
  };

  // "Back to Entrées": the selection stays in the cart, so nothing is lost.
  AlaCarteMenu.prototype.backToEntrees = function () {
    if (window.history.state && window.history.state.alcStep === 2) {
      window.history.back();
      return;
    }
    window.history.replaceState({ alcView: 'menu', alcStep: 1 }, '', window.location.pathname + window.location.search + '#menu');
    this.setStep(1, true);
  };

  /* ---------- analytics (Stape GTM container GTM-MBZDVXPX) ---------- */

  // The Stape container turns a "build_a_box" dataLayer event into GA4 view_item_list and
  // Meta tags. It used to read the legacy grid markup; this sends the same event and item
  // format for the products of the step on screen, once per step and product list.
  AlaCarteMenu.prototype.trackListView = function () {
    if (!this.zoneElement || document.documentElement.classList.contains('alc-flow-gated') &&
      !document.documentElement.classList.contains('alc-view-menu')) return;
    var panel = this.zoneElement.querySelector('[data-alc-step-panel="' + this.step() + '"]') || this.zoneElement;
    var seen = {};
    var items = [];
    var cards = panel.querySelectorAll('[data-alc-card]');
    for (var i = 0; i < cards.length; i++) {
      var product = this.products[String(cards[i].getAttribute('data-id'))];
      if (!product || seen[product.id]) continue;
      seen[product.id] = true;
      items.push({
        item_id: product.productId || product.id,
        item_sku: '',
        item_variant: product.id,
        item_name: product.title,
        variant_name: 'Default Title',
        item_category: '',
        item_brand: 'Mother of Macros',
        price: product.price / 100,
        imageURL: product.image,
        item_url: product.url,
        index: items.length + 1
      });
    }
    if (!items.length) return;
    var key = items.map(function (item) { return item.item_id; }).join(',');
    this.trackedLists = this.trackedLists || {};
    if (this.trackedLists[key]) return;
    this.trackedLists[key] = true;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'build_a_box',
      ecommerce: { currency: 'USD', items: items },
      actual_url: window.location.href,
      ecomm_pagetype: 'category'
    });
  };

  /* ---------- render ---------- */

  AlaCarteMenu.prototype.totals = function () {
    var count = 0;
    var subtotal = 0;
    for (var i = 0; i < this.productOrder.length; i++) {
      var id = this.productOrder[i];
      var quantity = this.quantities[id] || 0;
      count += quantity;
      subtotal += quantity * this.products[id].price;
    }
    // Other one-time items already in the cart (e.g. added on a product page) count too.
    var self = this;
    var other = 0;
    ((this.cart && this.cart.items) || []).forEach(function (item) {
      if (isAlcLine(item) || item.gift_card || isSubscriptionLine(item)) return;
      if (String(item.variant_id) === self.freezeVariantId) return;
      other += item.final_line_price || 0;
    });
    return { count: count, subtotal: subtotal, minimumTotal: subtotal + other };
  };

  AlaCarteMenu.prototype.minimum = function () {
    return window.MOMMinimum ? window.MOMMinimum.minimumCents() : (this.zone === 'local' ? 6000 : 17500);
  };

  AlaCarteMenu.prototype.render = function () {
    var self = this;
    var totals = this.totals();
    var minimum = this.minimum();
    var meetsMinimum = totals.count > 0 && totals.minimumTotal >= minimum;

    // Cards: Add button <-> stepper, quantity value.
    this.productOrder.forEach(function (id) {
      var quantity = self.quantities[id] || 0;
      self.products[id].cards.forEach(function (card) {
        card.classList.toggle('active', quantity > 0);
        var input = card.querySelector('[data-alc-qty]');
        if (input) input.value = quantity;
      });
    });
    this.renderDialogBuy();

    // Order panel items (entrées and desserts), in menu order.
    if (this.itemsElement) {
      var fragment = document.createDocumentFragment();
      this.productOrder.forEach(function (id) {
        var quantity = self.quantities[id];
        if (quantity) fragment.appendChild(self.buildPanelItem(self.products[id], quantity));
      });
      this.itemsElement.replaceChildren(fragment);
    }
    if (this.emptyElement) this.emptyElement.hidden = totals.count > 0;

    for (var i = 0; i < this.countElements.length; i++) {
      this.countElements[i].textContent = totals.count;
    }
    if (this.subtotalElement) this.subtotalElement.textContent = formatMoney(totals.subtotal, this.moneyFormat);

    // Progress bar: live total, both steps.
    if (this.minBar && window.MOMMinimum) window.MOMMinimum.renderBar(this.minBar, totals.minimumTotal, minimum);

    // Checkout (last step): grey until the minimum is reached.
    if (this.checkoutButton) {
      this.checkoutButton.disabled = !meetsMinimum;
      this.checkoutButton.classList.toggle('active', meetsMinimum);
      this.checkoutButton.setAttribute('aria-disabled', meetsMinimum ? 'false' : 'true');
    }
  };

  AlaCarteMenu.prototype.buildPanelItem = function (product, quantity) {
    var item = document.createElement('div');
    item.className = 'new-pdp-box-content-item';
    item.setAttribute('data-id', product.id);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'new-pdp-box-content-close alc-remove';
    remove.setAttribute('data-alc-action', 'remove');
    remove.setAttribute('data-id', product.id);
    remove.setAttribute('aria-label', 'Remove ' + product.title);
    remove.textContent = '✕';
    item.appendChild(remove);

    if (product.image) {
      var image = document.createElement('img');
      image.className = 'new-pdp-box-content-img';
      image.src = product.image;
      image.alt = '';
      image.width = 64;
      image.height = 64;
      image.loading = 'lazy';
      item.appendChild(image);
    }

    var title = document.createElement('p');
    title.className = 'new-pdp-box-content-title';
    title.appendChild(document.createTextNode(product.title));
    var quantityLine = document.createElement('span');
    quantityLine.textContent = 'x ' + quantity;
    title.appendChild(quantityLine);
    var price = document.createElement('span');
    price.className = 'new-pdp-box-content-price';
    price.textContent = formatMoney(product.price * quantity, this.moneyFormat);
    title.appendChild(price);
    item.appendChild(title);

    return item;
  };

  /* ---------- checkout ---------- */

  AlaCarteMenu.prototype.checkout = function () {
    var self = this;
    var totals = this.totals();
    if (totals.count === 0 || totals.minimumTotal < this.minimum()) return;

    if (this.checkoutButton) this.checkoutButton.classList.add('active-loading');
    // Finish any pending cart update, make sure exactly one Freeze Pack is in, then check out.
    this.sync().then(function () {
      return window.MOMEnforceFreezePack ? window.MOMEnforceFreezePack() : null;
    }).then(function () {
      return cartRequest('/cart.js');
    }).then(function (cart) {
      var result = window.MOMMinimum ? window.MOMMinimum.evaluate(cart) : { applies: true, met: true };
      if (result.applies && !result.met) {
        self.cart = cart;
        self.adoptCart();
        self.render();
        if (self.checkoutButton) self.checkoutButton.classList.remove('active-loading');
        return;
      }
      // Carry the stored gclid / UTM parameters (snippets/utm-persistence.liquid) to checkout.
      window.location.href = (window.MOMAttribution && window.MOMAttribution.decorate)
        ? window.MOMAttribution.decorate('/checkout')
        : '/checkout';
    }).catch(function () {
      if (self.checkoutButton) self.checkoutButton.classList.remove('active-loading');
      self.showError('Checkout is unavailable right now. Please try again.');
    });
  };

  AlaCarteMenu.prototype.showError = function (message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  };

  AlaCarteMenu.prototype.hideError = function () {
    if (this.errorElement) this.errorElement.hidden = true;
  };

  /* ---------- quick view ---------- */

  AlaCarteMenu.prototype.openDetails = function (id, opener) {
    var product = id && this.products[id];
    if (!product || !this.dialog || !this.dialogContent) {
      if (opener && opener.href) window.location.href = opener.href;
      return;
    }
    var template = product.cards[0].querySelector('template[data-alc-details]');
    if (!template) return;

    this.dialogProductId = id;
    this.dialogOpener = opener || null;
    this.dialogContent.replaceChildren(template.content.cloneNode(true));
    this.dialog.setAttribute('aria-label', product.title);
    this.renderDialogBuy();

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  };

  AlaCarteMenu.prototype.closeDetails = function () {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.dialog.dispatchEvent(new Event('close'));
    }
  };

  AlaCarteMenu.prototype.renderDialogBuy = function () {
    if (!this.dialogBuy || !this.dialogProductId) return;
    var quantity = this.quantityOf(this.dialogProductId);
    this.dialogBuy.classList.toggle('active', quantity > 0);
    var input = this.dialogBuy.querySelector('[data-alc-qty]');
    if (input) input.value = quantity;
  };

  /* ---------- mobile: keep the fixed order panel from covering the last cards ---------- */

  AlaCarteMenu.prototype.trackPanelHeight = function () {
    var self = this;
    if (!this.panel) return;
    function update() {
      var fixed = window.getComputedStyle(self.panel).position === 'fixed';
      self.root.style.setProperty('--alc-panel-height', fixed ? self.panel.offsetHeight + 'px' : '0px');
    }
    if (typeof ResizeObserver === 'function') new ResizeObserver(update).observe(this.panel);
    window.addEventListener('resize', update);
    update();
  };

  /* ---------- plan step (sections/build-a-box.liquid, rebuilt mode) ---------- */

  var VIEWS = ['zip', 'plan', 'menu'];

  function PlanFlow(root) {
    this.root = root;
    this.html = document.documentElement;
    this.localZips = readLocalZips(root);
    this.planWarning = root.querySelector('.plan__warning-text');
    this.bind();
    this.setView(this.currentView(), false);
  }

  PlanFlow.prototype.currentView = function () {
    for (var i = 0; i < VIEWS.length; i++) {
      if (this.html.classList.contains('alc-view-' + VIEWS[i])) return VIEWS[i];
    }
    return 'zip';
  };

  PlanFlow.prototype.setView = function (view, scroll) {
    var html = this.html;
    VIEWS.forEach(function (name) { html.classList.toggle('alc-view-' + name, name === view); });

    var crumbs = this.root.querySelectorAll('[data-alc-crumb]');
    var order = { plan: 0, menu: 1, checkout: 2 };
    var reached = view === 'menu' ? 1 : 0;
    for (var i = 0; i < crumbs.length; i++) {
      var step = order[crumbs[i].getAttribute('data-alc-crumb')];
      crumbs[i].classList.toggle('is-reached', step <= reached);
      if (step === reached) crumbs[i].setAttribute('aria-current', 'step');
      else crumbs[i].removeAttribute('aria-current');
    }

    if (scroll) window.scrollTo(0, 0);
  };

  PlanFlow.prototype.bind = function () {
    var self = this;
    var root = this.root;

    // ZIP step: the section's own script validates the ZIP, saves the cookie and clears
    // the cart (cart separation). Once the cookie is saved, move on to the plan step.
    var zipButtons = root.querySelectorAll('[data-next="1"]');
    for (var i = 0; i < zipButtons.length; i++) {
      zipButtons[i].addEventListener('click', function (event) {
        var button = event.currentTarget;
        var input = root.querySelector('#zip-enter input');
        var typed = input ? input.value.trim() : '';
        if (button.closest('#zip-enter') && !/^\d{5}$/.test(typed)) return;
        window.setTimeout(function () {
          var zip = getZip();
          if (!zip || (button.closest('#zip-enter') && zip !== typed)) return;
          var bar = document.querySelector('.new-box-delivery span strong');
          if (bar) bar.textContent = zip;
          self.setView('plan', true);
        }, 0);
      });
    }

    // Plan step: one decision per tap. A new choice comes from the radio's change event;
    // tapping the already-selected card (no change event) comes from the label click.
    var radios = root.querySelectorAll('input[name="plan"]');
    for (var r = 0; r < radios.length; r++) {
      radios[r].addEventListener('change', function (event) {
        self.choose(event.currentTarget);
      });
    }
    var cards = root.querySelectorAll('.plan.subscription-plan, .plan.one-time-plan');
    for (var c = 0; c < cards.length; c++) {
      cards[c].addEventListener('click', function (event) {
        if (event.target.closest('input')) return;
        var radio = event.currentTarget.querySelector('input[name="plan"]');
        if (radio && radio.checked) self.choose(radio);
      });
    }

    var nextStep = root.querySelector('[data-next="2"]');
    if (nextStep) {
      nextStep.addEventListener('click', function () {
        var checked = root.querySelector('input[name="plan"]:checked');
        if (checked) self.choose(checked);
        else if (self.planWarning) self.planWarning.textContent = 'PLEASE SELECT A PLAN BEFORE PROCEEDING.';
      });
    }

    // Breadcrumb "Plan" goes back to the plan step from the menu.
    root.addEventListener('click', function (event) {
      var crumb = event.target.closest('[data-alc-crumb="plan"]');
      if (!crumb || self.currentView() !== 'menu') return;
      event.preventDefault();
      self.backToPlan();
    });

    window.addEventListener('popstate', function () {
      if (MENU_HASHES.indexOf(window.location.hash) !== -1 && getZip()) self.setView('menu', false);
      else if (self.currentView() === 'menu') self.setView('plan', false);
    });
  };

  PlanFlow.prototype.choose = function (radio) {
    var cards = this.root.querySelectorAll('.plan-content');
    for (var i = 0; i < cards.length; i++) cards[i].classList.remove('checked');
    var card = radio.closest('.plan');
    var content = card && card.querySelector('.plan-content');
    if (content) content.classList.add('checked');
    if (this.planWarning) this.planWarning.textContent = '';

    if (radio.value === 'subscription') this.goToSubscription();
    else if (radio.value === 'one-time') this.showMenu();
  };

  // Same destinations as the legacy plan step (sections/build-a-box.liquid).
  PlanFlow.prototype.goToSubscription = function () {
    var isLocal = this.localZips.indexOf(getZip()) !== -1;
    var heroRedirect = new URLSearchParams(window.location.search).get('new-hero-redirect') === 'yes';
    var destination = heroRedirect
      ? (isLocal ? '/products/60-pack' : '/products/60-pack-nation-wide')
      : (isLocal ? '/pages/meal-plan-locals' : '/pages/meal-plan-nationwide');

    window.location.href = (window.MOMAttribution && window.MOMAttribution.decorate)
      ? window.MOMAttribution.decorate(destination)
      : destination;
  };

  // The menu always opens on step 1 (entrées).
  PlanFlow.prototype.showMenu = function () {
    if (window.location.hash !== '#menu') {
      window.history.pushState({ alcView: 'menu', alcStep: 1 }, '', window.location.pathname + window.location.search + '#menu');
    }
    var menu = document.querySelector('[data-alc-menu]');
    if (menu) menu.setAttribute('data-step', '1');
    this.setView('menu', true);
    document.dispatchEvent(new CustomEvent('alc:menu-shown'));
  };

  PlanFlow.prototype.backToPlan = function () {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    this.setView('plan', true);
  };

  /* ---------- init (also on theme editor section reloads) ---------- */

  function init(scope) {
    var menus = scope.querySelectorAll('[data-alc-menu]');
    for (var i = 0; i < menus.length; i++) {
      if (menus[i].hasAttribute('data-alc-ready')) continue;
      menus[i].setAttribute('data-alc-ready', '');
      new AlaCarteMenu(menus[i]);
    }
    // Theme editor: the plan step and the menu stay visible together (no step switching).
    var flows = window.Shopify && window.Shopify.designMode ? [] : scope.querySelectorAll('[data-alc-flow]');
    for (var j = 0; j < flows.length; j++) {
      if (flows[j].hasAttribute('data-alc-ready')) continue;
      flows[j].setAttribute('data-alc-ready', '');
      new PlanFlow(flows[j]);
    }
  }

  // mom-minimum.js is deferred in <head>, so it has run before this file.
  init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
